import React, { useEffect, useState, useRef, useCallback } from 'react';
import useOnMobile from 'src/hooks/useOnMobile';
import { RightArrow, LeftArrow } from './slider.arrows.styles';
import {
  ArrowContainer,
  DotsContainer,
  SliderContainer,
  SliderContent,
  SliderDot,
  SliderItem,
  SliderList,
  SliderListContainer,
} from './slider.styles';

export interface SliderConfiguration {
  infinite?: boolean;
  slidesToShow: number;
  draggable?: boolean;
  width?: number;
  arrows?: boolean;
  dots?: boolean;
  autoplaySpeed?: number;
  speed?: number;
  scrollRatioToChangeSlide?: number;
}

interface SliderProps extends SliderConfiguration {
  children: React.ReactChild[];
  responsive?: SliderConfiguration[];
  textColor?: string;
  arrowOverride?: {
    left?: () => JSX.Element;
    right?: () => JSX.Element;
  };
}

const Slider = (props: SliderProps): JSX.Element => {
  const isTablet = useOnMobile(1100);

  const { children, responsive } = props;

  const listContainerRef = useRef(null);
  const listRef = useRef(null);
  const [componentProps, setComponentProps] = useState<SliderConfiguration>(props);
  const [slideList, setSlideList] = useState<number[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideSize, setSlideSize] = useState(0);
  const [dragInfo, setDragInfo] = useState({
    isDragging: false,
    origin: 0,
    slidesTranslation: 0,
  });
  const draggingBlocker = useRef(false);
  const windowWidthRef = useRef<null | number>(null);

  const { slidesTranslation } = dragInfo;
  const {
    infinite,
    slidesToShow,
    draggable,
    arrows = true,
    dots = false,
    autoplaySpeed,
    speed,
    scrollRatioToChangeSlide = 0.5,
  } = componentProps;

  const handleWindowResize = (forceSlideListLoad = false) => {
    let sliderConfigProps: SliderConfiguration = props;
    let shouldAvoidSlideListReload = false;
    const windowWidth = window.innerWidth;

    if (windowWidthRef.current === windowWidth) return;

    windowWidthRef.current = windowWidth;

    if (responsive) {
      const responsiveProps = responsive.sort((a, b) => a.width - b.width);
      sliderConfigProps =
        responsiveProps.find((responsivePropItem) => {
          if (responsivePropItem.width > windowWidth) return responsivePropItem;
        }) ?? props;
    }

    setComponentProps((prev) => {
      if (prev.slidesToShow === sliderConfigProps.slidesToShow) {
        shouldAvoidSlideListReload = true;
      }
      return sliderConfigProps;
    });

    if (!shouldAvoidSlideListReload || forceSlideListLoad) {
      setSlideList(() => {
        const keysArray = Array.from(Array(children.length).keys());
        return infinite
          ? keysArray.map((_, i) => keysArray[(i + sliderConfigProps.slidesToShow + 1) % children.length])
          : keysArray;
      });
    }

    if (listContainerRef.current) {
      const listWidth = (listContainerRef.current as HTMLDivElement).getBoundingClientRect().width;
      setSlideSize(Math.trunc(listWidth / sliderConfigProps.slidesToShow));
    }
  };

  useEffect(() => {
    setActiveIndex((children.length - (slideList[0] % children.length)) % children.length);
  }, [children, slideList]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    setTimeout(() => handleWindowResize(true), 50);
    window.addEventListener('resize', () => handleWindowResize());

    return () => {
      window.removeEventListener('resize', () => handleWindowResize());
    };
  }, []);

  useEffect(() => {
    if (isAnimating) new Promise((resolve) => setTimeout(resolve, 600)).then(() => setIsAnimating(false));
  }, [isAnimating]);

  const handleNextClick = useCallback(
    (step = 1) => {
      if (!isAnimating) {
        setIsAnimating(true);
        setSlideList((prev) => {
          return prev.map((_, i) => prev[(i - step + slideList.length) % slideList.length]);
        });
      }
    },
    [isAnimating, slideList.length]
  );

  useEffect(() => {
    let interval: NodeJS.Timer | undefined;

    if (autoplaySpeed) {
      interval = setInterval(() => {
        if (!isPaused) {
          handleNextClick();
        }
      }, autoplaySpeed);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoplaySpeed, handleNextClick, isPaused]);

  const handlePrevClick = useCallback(
    (step = 1) => {
      if (!isAnimating) {
        setIsAnimating(true);
        setSlideList((prev) => {
          return prev.map((_, i) => prev[(i + step) % slideList.length]);
        });
      }
    },
    [isAnimating, slideList.length]
  );

  const buildSlide = useCallback(
    (position: number, index: number) => {
      const finalSlidesTranslation = Math.abs(slidesTranslation) < 30 && isTablet ? 0 : slidesTranslation;

      if (infinite) {
        return {
          translatePosition: position * slideSize - slideSize * (slidesToShow + 1) + finalSlidesTranslation,
          slide: children[index],
        };
      } else {
        return {
          translatePosition: (index - activeIndex) * slideSize + finalSlidesTranslation,
          slide: children[index],
        };
      }
    },
    [slideSize, slidesTranslation, slidesToShow, infinite, activeIndex]
  );

  const handleMouseDown = ({ clientX }: { clientX: number }) => {
    if (!draggingBlocker.current && draggable) {
      new Promise((resolve) => setTimeout(resolve, 100)).then(() => {
        draggingBlocker.current = true;

        setDragInfo({
          ...dragInfo,
          isDragging: true,
          origin: clientX,
        });
      });
    }
  };

  const handleMouseMove = ({ clientX }: { clientX: number }) => {
    if (draggingBlocker.current) {
      const { origin } = dragInfo;
      setDragInfo({
        ...dragInfo,
        slidesTranslation: clientX - origin,
      });
    }
  };

  const handleMouseUp = (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (event) event.stopPropagation();

    setTimeout(() => {
      if (draggingBlocker.current) {
        setDragInfo({
          ...dragInfo,
          isDragging: false,
        });

        draggingBlocker.current = false;

        handleDragStop();
      }
    }, 100);
  };

  const handleDragStop = () => {
    if (!slidesTranslation || !slideSize) return;

    const slideVariation = slidesTranslation / slideSize;

    setDragInfo({
      isDragging: false,
      origin: 0,
      slidesTranslation: 0,
    });

    draggingBlocker.current = false;

    if (Math.abs(slideVariation) > scrollRatioToChangeSlide) {
      const isFirstSlide = !infinite && activeIndex === 0;
      const isLastSlide = !infinite && activeIndex === children.length - slidesToShow;

      if (slideVariation > 0 && !isFirstSlide) {
        handlePrevClick();
      } else if (slideVariation < 0 && !isLastSlide) {
        handleNextClick();
      }
    }
  };

  const dotIsActive = useCallback(
    (index: number) => {
      if (!infinite) {
        return index === activeIndex;
      }

      return index === (activeIndex + slidesToShow + 1) % children.length;
    },
    [activeIndex, children, slidesToShow, infinite]
  );

  const handleClickDot = useCallback(
    (index: number) => {
      if (infinite) {
        const firstSlideIndex = (activeIndex + slidesToShow + 1) % children.length;

        if (index < firstSlideIndex) handlePrevClick(firstSlideIndex - index);
        if (index > firstSlideIndex) handleNextClick(index - firstSlideIndex);
      } else {
        if (index < activeIndex) handlePrevClick(activeIndex - index);
        if (index > activeIndex) handleNextClick(index - activeIndex);
      }
    },
    [slidesToShow, activeIndex, handlePrevClick, handleNextClick]
  );

  const handleSliderFocus = useCallback(() => {
    if (autoplaySpeed) {
      setIsPaused(true);
    }
  }, [autoplaySpeed]);

  const handleSliderBlur = useCallback(() => {
    if (autoplaySpeed) {
      setIsPaused(false);
    }
  }, [autoplaySpeed]);

  const renderSliderItem = (component: number, index: number) => {
    const slide = buildSlide(component, index);
    const isEndSlide = index === activeIndex || index === (activeIndex - 1 < 0 ? children.length - 1 : activeIndex - 1);
    const shouldRemoveOpacity = !draggingBlocker.current && draggable && infinite && isEndSlide;

    return (
      <SliderItem
        key={index}
        slideSize={slideSize}
        style={{ transform: `translateX(${slide.translatePosition}px)`, opacity: shouldRemoveOpacity ? '0' : '1' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={({ changedTouches }) => {
          const clientX = changedTouches[0].clientX;
          handleMouseDown({ clientX });
        }}
        onTouchMove={(event) => {
          event.stopPropagation();
          const clientX = event.changedTouches[0].clientX;
          handleMouseMove({ clientX });
        }}
        onTouchEnd={() => handleMouseUp()}
      >
        <div style={{ pointerEvents: dragInfo.isDragging ? 'none' : 'unset' }}>{slide.slide}</div>
      </SliderItem>
    );
  };

  const { left: LeftArrowOverride, right: RightArrowOverride } = props.arrowOverride || {};

  const LeftArrowIcon = LeftArrowOverride ? <LeftArrowOverride /> : <LeftArrow color={props.textColor} />;
  const RightArrowIcon = RightArrowOverride ? <RightArrowOverride /> : <RightArrow color={props.textColor} />;

  return (
    <SliderContainer>
      <SliderContent className='slider-content'>
        {arrows && (
          <ArrowContainer
            className='arrow-container arrow-left-container'
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              if (!infinite && activeIndex === 0) return;

              handlePrevClick();
            }}
          >
            {LeftArrowIcon}
          </ArrowContainer>
        )}
        <SliderListContainer ref={listContainerRef}>
          <SliderList
            ref={listRef}
            isDragging={draggingBlocker.current}
            speed={speed}
            onFocus={handleSliderFocus}
            onBlur={handleSliderBlur}
            tabIndex={0}
            className='slider-list'
          >
            {infinite
              ? slideList.map((component, index) => renderSliderItem(component, index))
              : children.map((_, index) => renderSliderItem(index, index))}
          </SliderList>
        </SliderListContainer>
        {arrows && (
          <ArrowContainer
            className='arrow-container arrow-right-container'
            onClick={(event) => {
              event.stopPropagation();
              if (!infinite && activeIndex === children.length - slidesToShow) return;

              handleNextClick();
            }}
          >
            {RightArrowIcon}
          </ArrowContainer>
        )}
      </SliderContent>
      {dots && (
        <DotsContainer className='dots-container'>
          {children.map((_, index) => {
            if (!infinite) {
              if (index > children.length - slidesToShow) return null;
            }

            const isActive = dotIsActive(index);

            return (
              <SliderDot
                className={`dot-item ${isActive ? 'dot-active' : ''}`}
                key={index}
                isActive={isActive}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClickDot(index);
                }}
              />
            );
          })}
        </DotsContainer>
      )}
    </SliderContainer>
  );
};

export default Slider;
